import { InjectionToken } from '@angular/core';

/**
 * App-level configuration for `@sisoma/auth`, populated per surface by
 * `provideAuth()` (see `../http/provide-auth`).
 */
export interface AuthConfig {
  /**
   * Where `SessionService.redirectToLogin()` sends the browser. Apps override
   * this with the absolute shared-login URL (Phase 1: `app.halio.ai/sign-in`);
   * the default is a same-origin relative path.
   */
  loginUrl: string;
  /**
   * Base URL of the API. `authInterceptor` only touches requests whose URL
   * starts with this value (cookie credentials + refresh-on-401). The default
   * is empty, which leaves the interceptor inert until an app configures it.
   */
  apiUrl: string;
  /**
   * Where the route guards send a user who is logged in but lacks the required
   * capability / role / partner access (a not-logged-in user goes to
   * {@link loginUrl} instead). A same-origin router path; the default is the
   * app root. Apps can point it at a dedicated 403 route.
   */
  forbiddenUrl: string;
  /**
   * Where `requireCapabilities(...)` (and `requireAnyCapability(...)`) sends a
   * logged-in user who lacks the capability, when that should differ from {@link forbiddenUrl}. Defaults to
   * {@link forbiddenUrl}, so an app that does not set it is unaffected.
   *
   * The split exists because the two denials have different reach.
   * `requirePartnerAccess` and `requireRole` gate a whole surface, so their
   * target has to sit outside the guarded shell — inside it, the redirect
   * re-triggers the guard that just denied and loops. A capability denial
   * happens to someone who has *already* passed that gate, so its target can
   * live inside the shell, where an app's own membership gate can explain an
   * empty capability set far better than a generic 403 page.
   */
  capabilityDeniedUrl?: string;
}

export const AUTH_DEFAULTS: AuthConfig = {
  loginUrl: '/sign-in',
  apiUrl: '',
  forbiddenUrl: '/',
};

/**
 * Injection token for {@link AuthConfig}. Defaults to {@link AUTH_DEFAULTS} so
 * the library works unconfigured (and in tests); `provideAuth()` overrides it
 * per app.
 */
export const AUTH_CONFIG = new InjectionToken<AuthConfig>('AUTH_CONFIG', {
  providedIn: 'root',
  factory: () => AUTH_DEFAULTS,
});
